<template>
    <div class="general-comment">
        <div
            v-if="!editable"
            class="general-comment__readonly"
            v-html="value"
        ></div>
        <div v-else>
            <div class="general-comment__editor-wrap" data-cy="generalCommentTextarea">
                <vue-editor
                    v-model="text"
                    :rows="rows"
                    class="general-comment__editor w-full"
                />
            </div>
            <div class="general-comment__meta flex items-center justify-between mt-1">
                <span
                    class="general-comment__counter text-xs"
                    :class="{ 'text-red': !$v.text.minWords }"
                    data-cy="generalCommentCounter"
                >
                    {{ wordCount }} / {{ minimumWords }} words
                </span>
                <p
                    v-if="showError"
                    class="general-comment__error text-xs text-red m-0"
                    data-cy="generalCommentError"
                >
                    Minimum {{ minimumWords }} words required before the assessment can be submitted
                </p>
            </div>
            <div v-if="showSubmit" class="flex flex-row justify-end mt-2">
                <el-button
                    type="success"
                    :disabled="saving"
                    data-cy="generalCommentSubmit"
                    @click="onSubmit"
                >
                    Save
                </el-button>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Phase 2 / §2.6 — Story 6.1 — Self-contained General Comment editor.
 *
 * Pairs with the BE Story 6.2 validator (web.generalcomment serializer)
 * — they share the 25-word minimum and the same word-count semantics
 * via the FE/BE parity fixture (T-2.6.2-U-05). This component is
 * intentionally agnostic about transport: the parent (ModalSummary)
 * handles the HTTP POST/PATCH to web.generalcomment after the
 * `submit` event fires.
 *
 * The 25-word minimum is a SUBMISSION rule, not a save rule. A
 * surveyor may save a work-in-progress comment of any length so the
 * draft is never lost; the word count is surfaced here purely as
 * advisory feedback (counter turns red + inline message). The rule is
 * enforced where it matters — `Assessment.vue invalidResponsesObj`
 * blocks assessment submission until the surveyor's own saved comment
 * clears the minimum.
 */
import { VueEditor } from 'vue2-editor'
import { countMeaningfulWords, minWords } from '@/utils/wordCount'

const DEFAULT_MIN_WORDS = 25

export default {
    name: 'GeneralComment',
    components: { VueEditor },
    props: {
        value: {
            type: String,
            default: '',
        },
        editable: {
            type: Boolean,
            default: true,
        },
        saving: {
            type: Boolean,
            default: false,
        },
        rows: {
            type: Number,
            default: 12,
        },
        showSubmit: {
            type: Boolean,
            default: true,
        },
        minimumWords: {
            type: Number,
            default: DEFAULT_MIN_WORDS,
        },
    },
    data() {
        return {
            text: this.value,
        }
    },
    watch: {
        // Parent prop changes flow in; guard against the re-entry the
        // local `text` watcher would otherwise cause.
        value(newValue) {
            if (newValue !== this.text) this.text = newValue
        },
        // Local edits flow out. Vuelidate state updates automatically
        // because it watches `text` via the validations() function.
        text(newValue) {
            this.$emit('input', newValue)
        },
    },
    computed: {
        wordCount() {
            return countMeaningfulWords(this.text)
        },
        // Below the submission minimum. Does NOT gate saving — see the
        // component docblock.
        isInvalid() {
            return this.$v.text.$invalid
        },
        // Show the inline error eagerly — on initial mount with a
        // too-short value, the surveyor needs to know without first
        // touching the field. Vuelidate's `$dirty` gate would suppress
        // that signal.
        showError() {
            return this.isInvalid
        },
    },
    validations() {
        return {
            text: {
                minWords: minWords(this.minimumWords),
            },
        }
    },
    methods: {
        // Saving is unconditional — short drafts are allowed to persist.
        onSubmit() {
            this.$emit('submit', this.text)
        },
    },
}
</script>
